/**
 * Search Drawer - Predictive Search Component
 * Opens from header search bar, fetches rendered product-card-mini snippets
 * via Section Rendering API with debounced requests
 * Matches cart drawer design exactly
 * ============================================
 */

(function() {
  'use strict';

  const DEBOUNCE_DELAY = 300;
  const MIN_QUERY_LENGTH = 2;

  class SearchDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-search-drawer]');
      if (!this.drawer) return;

      this.overlay = this.drawer.querySelector('[data-search-overlay]');
      this.closeBtn = this.drawer.querySelector('[data-search-close]');
      this.input = this.drawer.querySelector('[data-search-drawer-input]');
      this.clearBtn = this.drawer.querySelector('[data-search-clear]');
      this.body = this.drawer.querySelector('[data-search-drawer-body]');
      
      // State containers
      this.initialState = this.drawer.querySelector('[data-search-initial]');
      this.loadingState = this.drawer.querySelector('[data-search-loading]');
      this.resultsContainer = this.drawer.querySelector('[data-search-results]');
      
      // Footer link
      this.fullSearchLink = this.drawer.querySelector('[data-search-full-link]');

      this.debounceTimer = null;
      this.abortController = null;
      this.isOpen = false;
      this.currentQuery = '';

      this.init();
    }

    init() {
      // Open/close events
      this.overlay?.addEventListener('click', () => this.close());
      this.closeBtn?.addEventListener('click', () => this.close());
      
      // Input events
      this.input?.addEventListener('input', (e) => this.handleInput(e));
      this.clearBtn?.addEventListener('click', () => this.clearInput());
      
      // Keyboard events
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Listen for open events from search bar
      window.addEventListener('search:open-drawer', () => this.open());
      
      // Re-initialize Lucide icons when drawer opens
      this.drawer.addEventListener('transitionend', () => {
        if (this.isOpen && typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      });
    }

    open() {
      if (this.isOpen) return;
      
      this.isOpen = true;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus input after transition
      setTimeout(() => {
        this.input?.focus();
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      }, 100);
    }

    close() {
      if (!this.isOpen) return;
      
      this.isOpen = false;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Cancel any pending requests
      this.abortController?.abort();
    }

    clearInput() {
      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }
      this.clearBtn.style.display = 'none';
      this.showState('initial');
      this.currentQuery = '';
    }

    handleInput(e) {
      const query = e.target.value.trim();
      
      // Toggle clear button
      this.clearBtn.style.display = query.length > 0 ? 'flex' : 'none';
      
      // Clear existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // Cancel pending request
      this.abortController?.abort();
      
      // Don't search for short queries
      if (query.length < MIN_QUERY_LENGTH) {
        this.showState('initial');
        this.currentQuery = '';
        return;
      }
      
      // Show loading state
      this.showState('loading');
      
      // Debounce the search
      this.debounceTimer = setTimeout(() => {
        this.performSearch(query);
      }, DEBOUNCE_DELAY);
    }

    async performSearch(query) {
      this.currentQuery = query;
      this.abortController = new AbortController();
      
      try {
        // Use Section Rendering API to get rendered product-card-mini snippets
        const url = new URL('/search/suggest', window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        
        const response = await fetch(url.toString(), {
          signal: this.abortController.signal
        });
        
        if (!response.ok) {
          throw new Error('Search request failed');
        }
        
        const html = await response.text();
        this.renderResults(html, query);
        
      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was cancelled, ignore
          return;
        }
        console.error('Search error:', error);
        this.showState('initial');
      }
    }

    renderResults(html, query) {
      // Parse the HTML response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the predictive search content
      const predictiveSearch = doc.querySelector('[data-predictive-search]');
      
      if (!predictiveSearch) {
        this.showState('initial');
        return;
      }
      
      // Check if there are results or no-results state
      const results = predictiveSearch.querySelector('[data-predictive-results]');
      const noResults = predictiveSearch.querySelector('[data-no-results]');
      
      if (results) {
        // Insert the results HTML
        this.resultsContainer.innerHTML = results.innerHTML;
        this.showState('results');
        
        // Update full search link
        if (this.fullSearchLink) {
          this.fullSearchLink.href = `/search?q=${encodeURIComponent(query)}`;
        }
        
        // Initialize components
        this.initializeResultComponents();
        
      } else if (noResults) {
        // Insert no results HTML
        this.resultsContainer.innerHTML = noResults.outerHTML;
        this.showState('results');
        
        // Re-initialize Lucide icons
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      } else {
        this.showState('initial');
      }
    }

    initializeResultComponents() {
      // Re-initialize Lucide icons for new content
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
      
      // Initialize product-card-mini components (variant pickers, add to cart)
      if (window.ProductCardMini && typeof window.ProductCardMini.init === 'function') {
        window.ProductCardMini.init();
      }
      
      // Initialize select components
      if (typeof window.initSelects === 'function') {
        window.initSelects();
      }
      
      // Re-init any custom selects in the results
      const selects = this.resultsContainer.querySelectorAll('.select:not([data-initialized])');
      selects.forEach(select => {
        if (window.ArcoSelect) {
          new window.ArcoSelect(select);
        }
      });
      
      // Initialize title marquees for long titles
      this.initTitleMarquees();
    }

    initTitleMarquees() {
      const titleWrappers = this.resultsContainer.querySelectorAll('[data-title-wrapper]');
      
      titleWrappers.forEach(wrapper => {
        const titleEl = wrapper.querySelector('[data-title]');
        if (!titleEl) return;
        
        // Check if text overflows
        const isOverflowing = titleEl.scrollWidth > wrapper.clientWidth;
        
        if (isOverflowing) {
          wrapper.classList.add('is-marquee');
          
          // Calculate animation duration based on text length
          const textWidth = titleEl.scrollWidth;
          const duration = textWidth / 30; // 30px per second
          
          if (typeof gsap !== 'undefined') {
            gsap.to(titleEl, {
              x: -textWidth / 2,
              duration: duration,
              ease: 'none',
              repeat: -1,
              repeatDelay: 1
            });
          }
        }
      });
    }

    showState(state) {
      // Hide all states
      this.initialState.style.display = 'none';
      this.loadingState.style.display = 'none';
      this.resultsContainer.style.display = 'none';
      
      // Show requested state
      switch (state) {
        case 'initial':
          this.initialState.style.display = 'flex';
          break;
        case 'loading':
          this.loadingState.style.display = 'flex';
          break;
        case 'results':
          this.resultsContainer.style.display = 'flex';
          break;
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new SearchDrawer());
  } else {
    new SearchDrawer();
  }

  // Expose globally for external access
  window.SearchDrawer = SearchDrawer;
})();
